import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Check, Lock } from "lucide-react";
import PublicLayout, { Eyebrow, PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { ROUTES, EXTERNAL, C, SERIF } from "@/lib/publicLinks";
import { DIRECTED_TREATMENTS, HSA_NOTE } from "@/lib/serviceCategories";
import { useOfferLinks } from "@/lib/useOfferLinks";

// Four distinct journeys — never interchangeable.
const JOURNEYS = [
  { t: "Free New Client Assessment", d: "A few free questions that recommend a starting point. Not a clinical assessment.", to: ROUTES.navigator, label: "Take the free questions" },
  { t: "Paid Virtual Assessment", d: "A detailed online KISSED Wellness Assessment™ completed from home, with findings and recommendations.", to: "/wellness#choose-depth", label: "Compare virtual assessments" },
  { t: "Microscopic Hair & Scalp Analysis", d: "In-clinic, magnified analysis of the hair and scalp.", hrefKey: "micro", label: "Book in-clinic analysis" },
  { t: "KISSED Hair Loss Investigation™", d: "Our most comprehensive in-clinic hair & scalp investigation.", hrefKey: "investigation", label: "Book the Investigation" },
];

const Assessments = ({ links }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-6 items-start scroll-mt-28" id="assessments">
    <div className="rounded-2xl p-7 bg-white lg:col-span-2" style={{ border: `1px solid ${C.primary}` }} data-testid="hair-investigation-card">
      <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>Step One · Investigate</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900" style={SERIF}>KISSED Hair Loss Investigation™</div>
      <div className="mt-1 text-sm font-medium text-slate-800" data-testid="hair-investigation-price">In-clinic · $499</div>
      <p className="mt-3 text-slate-600">Comprehensive history review, microscopic hair and scalp analysis, ZYTO biometric wellness screening, findings review, and personalized next-step recommendations. This is the recommended starting point for most new hair and scalp clients.</p>
      <p className="mt-3 text-sm text-slate-500">Prefer to begin from home? A paid virtual assessment is a separate online option — in-clinic microscopic analysis is not included virtually.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <PrimaryBtn to="/wellness#choose-depth" data-testid="hair-virtual-assessment">Start My Virtual Hair Loss Assessment <ArrowRight className="h-4 w-4" /></PrimaryBtn>
        <GhostBtn href={links.investigation} data-testid="hair-book-investigation">Book My Investigation</GhostBtn>
      </div>
    </div>
    <div className="rounded-2xl p-7 bg-white" style={{ border: `1px solid ${C.line}` }} data-testid="hair-micro-card">
      <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>Prefer Something Focused?</div>
      <div className="mt-2 text-xl font-semibold text-slate-900" style={SERIF}>Microscopic Hair &amp; Scalp Analysis</div>
      <div className="mt-1 text-sm font-medium text-slate-800" data-testid="hair-micro-price">In-clinic · $250 · with ZYTO scan $350</div>
      <p className="mt-3 text-sm text-slate-600">A focused, magnified look at what's happening with the hair and scalp — without completing the entire Investigation — with personalized recommendations.</p>
      <div className="mt-6"><GhostBtn href={links.micro} className="w-full" data-testid="hair-book-analysis">Book Hair &amp; Scalp Analysis</GhostBtn></div>
    </div>
    <p className="lg:col-span-3 text-sm text-slate-500" data-testid="hair-hsa-note">{HSA_NOTE}</p>
  </section>
);

const Journeys = ({ links }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
    <H2>Four different starting points</H2>
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {JOURNEYS.map((j, i) => (
        <div key={j.t} className="rounded-2xl p-5" style={{ border: `1px solid ${C.line}` }} data-testid={`hair-journey-${i}`}>
          <div className="font-semibold text-slate-900">{j.t}</div>
          <p className="mt-2 text-sm text-slate-600">{j.d}</p>
          {j.hrefKey
            ? <a href={links[j.hrefKey]} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm font-semibold" style={{ color: C.primary }}>{j.label} →</a>
            : <a href={j.to} className="mt-3 inline-block text-sm font-semibold" style={{ color: C.primary }}>{j.label} →</a>}
        </div>
      ))}
    </div>
  </section>
);

const Restore = () => (
  <section style={{ background: C.lightBg }}>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="text-xs font-semibold uppercase tracking-widest flex items-center gap-2" style={{ color: C.accent }}><Lock className="h-3.5 w-3.5" /> After Your Findings · Practitioner-Directed</div>
      <H2 className="mt-2">12-Week Hair &amp; Scalp Restoration Program</H2>
      <p className="mt-4 text-slate-600 max-w-2xl">Personalized support built around your own findings — not a self-selected package. It is offered only when your assessment identifies it as an appropriate next step.</p>
      <p className="mt-4 text-sm text-slate-600 max-w-2xl">{DIRECTED_TREATMENTS.copy}</p>
      <ul className="mt-4 grid sm:grid-cols-2 gap-2 max-w-2xl">
        {DIRECTED_TREATMENTS.items.map((t) => <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 mt-0.5" style={{ color: C.green }} />{t}</li>)}
      </ul>
      <div className="mt-6"><PrimaryBtn to="/hair-scalp#assessments" data-testid="hair-directed-cta">{DIRECTED_TREATMENTS.ctaLabel} <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
    </div>
  </section>
);

export default function HairScalp() {
  const { hash } = useLocation();
  const links = useOfferLinks({ investigation: EXTERNAL.bookHairInvestigation, micro: EXTERNAL.bookHairScalpAnalysis });
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [hash]);
  return (
    <PublicLayout>
      <section style={{ background: C.lightBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow>Hair &amp; Scalp</Eyebrow>
          <h1 className="text-4xl sm:text-5xl leading-[1.05] text-slate-900 max-w-3xl" style={SERIF} data-testid="hair-h1">Your Hair May Be Telling You Something.</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">Shedding, thinning, and scalp changes may be influenced by multiple factors — and deserve a broader investigation, not just a closer look.</p>
        </div>
      </section>
      <Assessments links={links} />
      <Journeys links={links} />
      <Restore />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <H2>Not sure which fits?</H2>
        <p className="mt-3 text-slate-600">Answer a few free questions and we'll suggest a starting point.</p>
        <div className="mt-6 flex justify-center"><PrimaryBtn to={ROUTES.navigator} data-testid="hair-free-navigator">Take the Free New Client Assessment <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
      </section>
    </PublicLayout>
  );
}
